import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import {
  cropConnectApiClient,
  CropConnectLanguages,
  CropConnectUserDto,
  RoleAssignmentDto,
  SelectableBusinessPartnerDto,
} from '../services/cropConnectApiClient';

// All properties are set through fetchContext instead of doing the local modifications
export interface ContextState {
  cropConnectUserDisplayName?: string | undefined;
  globalAssignment?: RoleAssignmentDto | undefined;
  businessPartnerAssignment?: RoleAssignmentDto | undefined;
  businessPartners: SelectableBusinessPartnerDto[];
  activeBpNumber?: string;
  activeBpNumberCountry?: string;
  isAccessOnBehalf?: boolean;
  isInitialLogin?: boolean;
  nordzuckerInternalUser: boolean;
  accessibleBpRanges?: AccessibleBusinessPartnerRange[] | undefined;
  contextUpdating: boolean;
  user: User | null;
  selectedUserLanguage: CropConnectLanguages;
  appInsightsContextData: { [key: string]: string } | undefined;
  fetchContext: () => Promise<void>;
  fetchUser: () => Promise<void>;
  updateUserDisplayName: (newDisplayName: string) => Promise<void>;
  switchBusinessPartner: (
    bpNumber: string,
    accessOnBehalf: boolean
  ) => Promise<void>;
  finishAccessOnBehalf: () => Promise<void>;
  isContextReady: () => boolean;
  userRolesLoaded: boolean;
  enabledFeatures: string[] | undefined;
}

export interface AccessibleBusinessPartnerRange {
  low: number;
  high?: number | undefined;
}

interface LocalStorageContextState {
  cropConnectUserDisplayName?: string | undefined;
  assignments?: RoleAssignmentDto[] | undefined;
  activeBpNumber?: string;
  nordzuckerInternalUser: boolean;
  accessibleBpRanges?: AccessibleBusinessPartnerRange[] | undefined;
  user: User | null;
  selectedUserLanguage: CropConnectLanguages;
}

interface User {
  id: string;
  displayName: string;
  email: string;
}

const transformUserDTO = (userDTO: CropConnectUserDto): User => ({
  id: userDTO.id || '',
  displayName: userDTO.displayName || '',
  email: userDTO.email || '',
});

const localStorageContextKey = 'cc.context';

const updateContextFromApi = async (
  set: <A extends string | { type: string }>(
    partial:
      | ContextState
      | Partial<ContextState>
      | ((state: ContextState) => ContextState | Partial<ContextState>),
    replace?: boolean | undefined,
    action?: A | undefined
  ) => void
) => {
  set({ userRolesLoaded: false });
  // Fetch the current context from the API
  const contextResponse = await cropConnectApiClient.getCurrentContext();

  if (contextResponse.success && contextResponse.data) {
    const data = contextResponse.data;

    // Check if the selectedLanguage from API is valid
    const isValidLanguage =
      data.selectedLanguage &&
      Object.values(CropConnectLanguages).includes(data.selectedLanguage);

    // Determine the selected language, defaulting to the fallback if invalid
    const selectedLanguage = isValidLanguage
      ? data.selectedLanguage
      : process.env.REACT_APP_FALLBACK_CROPCONNECT_LANGUAGE ||
        CropConnectLanguages.EnGB;

    // Build the context object with the updated data
    const contextObject = {
      globalAssignment: data.globalRoleAssignment,
      businessPartnerAssignment: data.businessPartnerRoleAssignment,
      businessPartners: data.businessPartners ?? [],
      isAccessOnBehalf: data.isAccessOnBehalfActive,
      activeBpNumber: data.activeBpNumber,
      activeBpNumberCountry: data.activeBpNumberCountry,
      cropConnectUserDisplayName: data.userDisplayName,
      nordzuckerInternalUser: data.nordzuckerInternalUserId !== undefined,
      selectedUserLanguage: selectedLanguage,
      isInitialLogin: data.isInitialLogin,
      accessibleBpRanges: data.accessibleBusinessPartnerRanges?.map((ab) => ({
        low: ab.low,
        high: ab.high,
      })),
      contextUpdating: false,
      appInsightsContextData: data.analyticsContext,
      userRolesLoaded: true,
      enabledFeatures: data.enabledFeatures,
    } as Partial<ContextState>;
    // Save the updated context to localStorage
    saveContextDataToLocalStorage(contextObject);

    // Update the state with the new context data
    set(contextObject);
  } else {
    // Error handling for when the context fetch fails
    // Set contextUpdating to false to reset the state
    set({ contextUpdating: false });
  }
};

const switchBusinessPartnerUpdateContext = async (
  set: <A extends string | { type: string }>(
    partial:
      | ContextState
      | Partial<ContextState>
      | ((state: ContextState) => ContextState | Partial<ContextState>),
    replace?: boolean | undefined,
    action?: A | undefined
  ) => void,
  get: () => ContextState,
  bpNumber: string,
  accessOnBehalf: boolean
) => {
  try {
    set({ contextUpdating: true });
    await cropConnectApiClient.switchBusinessPartner(bpNumber, accessOnBehalf);
    await updateContextFromApi(set);
  } catch (error) {
    console.error('Error during switchBusinessPartner:', error);
    throw error;
  } finally {
    set({ contextUpdating: false });
  }
};

const saveContextDataToLocalStorage = (obj: Partial<ContextState>) => {
  const contextObject = {
    globalAssignment: obj.globalAssignment,
    businessPartnerAssignment: obj.businessPartnerAssignment,
    businessPartners: obj.businessPartners,
    isAccessOnBehalf: obj.isAccessOnBehalf,
    activeBpNumber: obj.activeBpNumber,
    cropConnectUserDisplayName: obj.cropConnectUserDisplayName,
    nordzuckerInternalUser: obj.nordzuckerInternalUser,
    selectedUserLanguage: obj.selectedUserLanguage,
    accessibleBpRanges: obj.accessibleBpRanges,
    contextUpdating: obj.contextUpdating,
  };
  localStorage.setItem(localStorageContextKey, JSON.stringify(contextObject));
};

const contextObject = ((): LocalStorageContextState => {
  const storageObjectString = localStorage.getItem(localStorageContextKey);
  if (storageObjectString != null) {
    try {
      return JSON.parse(storageObjectString);
    } catch {
      // Noop
    }
  }
  return {
    nordzuckerInternalUser: false,
    user: null,
    selectedUserLanguage:
      CropConnectLanguages?.EnGB ??
      process.env.REACT_APP_FALLBACK_CROPCONNECT_LANGUAGE,
  };
})();

/** ContextStore should be the central source of truth for the other contexts */
export const useContextStore = create<ContextState>()(
  devtools((set, get) => ({
    nordzuckerInternalUser: contextObject.nordzuckerInternalUser ?? false,
    businessPartners: [],
    contextUpdating: false,
    user: contextObject.user,
    selectedUserLanguage: contextObject.selectedUserLanguage,
    appInsightsContextData: {},
    userRolesLoaded: false,
    enabledFeatures: undefined,
    fetchContext: async () => {
      try {
        if (get().contextUpdating === true) {
          console.debug('Context already updating. Will skip');
        } else {
          console.debug('Will update context!');
          set({ contextUpdating: true });
          await updateContextFromApi(set);
        }
      } catch (e) {
        console.error(e); // TODO: is this a real error or completely normal when logged out?
      } finally {
        set({ contextUpdating: false });
      }
    },
    fetchUser: async () => {
      try {
        const response = await cropConnectApiClient.getLoggedInUser();
        if (response.success && response.data) {
          const user = transformUserDTO(response.data);
          set({ user });
        } else {
          console.error('Failed to fetch user:', response.error?.message);
        }
      } catch (error) {
        console.error('Error fetching user:', error);
      }
    },
    updateUserDisplayName: async (newName: string) => {
      try {
        const response =
          await cropConnectApiClient.changeMyDisplayName(newName);
        if (response.success && response.data) {
          const user = transformUserDTO(response.data);
          set({ user });
        } else {
          console.error('Failed to fetch user:', response.error?.message);
        }
      } catch (e) {
        console.error('Error updating user name:', e);
      }
    },
    switchBusinessPartner: async (
      bpNumber: string,
      accessOnBehalf: boolean
    ) => {
      cropConnectApiClient.abortRunningRequests('Business Partner changing');
      if (accessOnBehalf) {
        try {
          set({ contextUpdating: true });
          await cropConnectApiClient.switchBusinessPartner(
            bpNumber,
            accessOnBehalf
          );
          await updateContextFromApi(set);
        } catch (error) {
          console.error('Error during switchBusinessPartner:', error);
          throw error;
        } finally {
          set({ contextUpdating: false });
        }
      } else {
        try {
          set({ contextUpdating: true });
          await switchBusinessPartnerUpdateContext(
            set,
            get,
            bpNumber,
            accessOnBehalf
          );
        } finally {
          set({ contextUpdating: false });
        }
      }
    },
    finishAccessOnBehalf: async () => {
      await cropConnectApiClient.finishAccessOnBehalf();
      set({ contextUpdating: true });
      await updateContextFromApi(set);
    },
    isContextReady: () => {
      const state = get();
      return state.userRolesLoaded;
    },
  }))
);
